import React, { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import InputFormField from "components/input-form-field";
import ModalForm from "components/modal-form";
import SwitchFormField from "components/switch-form-field";
import { PhoneField } from "features/patients/patient-form/fields";
import { ReferralInput, useSendReferralMutation } from "services/referrals";
import { EMAIL_REGEX } from "utils/validations.utils";

interface ReferralsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReferralsModal: FC<ReferralsModalProps> = ({ onClose, isOpen }) => {
  const dispatch = useAppDispatch();

  const [sendReferral, { isLoading }] = useSendReferralMutation();

  const formMethods = useForm<ReferralInput>({
    mode: "onBlur",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      can_mention: false,
      company: "",
      website: "",
      phone: ""
    }
  });

  const { handleSubmit, control, reset } = formMethods;

  const onSubmit = (data: ReferralInput) => {
    sendReferral(data).then((response) => {
      if ("error" in response) {
        dispatch(
          setCustomMessage(<ErrorSnackbarContent response={response} />)
        );
        dispatch(setOpen(true));
      } else {
        dispatch(setCustomMessage("The Referral  Has Been Created."));
        dispatch(setOpen(true));
        reset();
        onClose();
      }
    });
  };

  return (
    <ModalForm
      isOpen={isOpen}
      isSubmitting={isLoading}
      onCancel={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title="Referrals"
      hideBackdrop={false}
      control={control}
      submitButtonLabel="Submit"
    >
      <FormProvider {...formMethods}>
        <Typography display="block" variant="body3" fontWeight={700} mb={1}>
          At Suno, we value the trust and support of our partners.
        </Typography>
        <Typography variant="body2">
          As a token of our appreciation, we’re pleased to offer a $500
          incentive for each practice you refer that successfully transitions to
          our platform.
        </Typography>
        <Typography variant="body2" mb={4}>
          Help your colleagues enhance their practice management experience with
          Suno.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InputFormField
              name="first_name"
              label="First Name"
              control={control}
              required
              sx={{}}
            />
          </Grid>
          <Grid item xs={6}>
            <InputFormField
              name="last_name"
              label="Last Name"
              control={control}
              required
              sx={{}}
            />
          </Grid>
          <Grid item xs={6}>
            <PhoneField name="phone" control={control} sx={{}} />
          </Grid>
          <Grid item xs={6}>
            <InputFormField
              name="email"
              label="Email"
              control={control}
              rules={{
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Email Address is not valid."
                }
              }}
              sx={{}}
            />
          </Grid>
          <Grid item xs={6}>
            <InputFormField
              name="company"
              label="Company Name"
              control={control}
              sx={{}}
            />
          </Grid>
          <Grid item xs={6}>
            <InputFormField
              name="website"
              label="Website"
              control={control}
              sx={{}}
            />
          </Grid>
          <Grid item xs={12}>
            <SwitchFormField
              label="Can we let them know you referred them?"
              name="can_mention"
            />
          </Grid>
        </Grid>
      </FormProvider>
    </ModalForm>
  );
};

export default ReferralsModal;
